/**
 * Issue trackers through the platform (packages/shared/src/schemas/trackers.ts): the trackers a
 * project's source names, how each stands, a reconciliation on demand, and for owners the form
 * that adds one. Sits under Source on a project's Source tab.
 */

import { TRACKER_PROVIDERS, trackerTargetKey, type LocalBoardStanding, type ProjectSource, type ProjectTracker, type TrackerProvider, type TrackerStanding, type TrackerStatus } from '@aioe/shared';
import { ArrowsRotateRight, Display } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const PROVIDER_TEXT: Record<TrackerProvider, string> = { 'github-issues': 'GitHub Issues', 'azure-boards': 'Azure Boards', jira: 'Jira' };
const BOARD_TEXT: Record<TrackerStatus['board'], string> = { backlog: 'Backlog', todo: 'To do', 'in-progress': 'In progress', review: 'Review', blocked: 'Blocked', done: 'Done' };
const DIRECTION_TEXT = { 'two-way': 'Two-way', import: 'Import only' } as const;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The boards kept on a workbench, for the project's trackers and queue. A quiet failure reads as none. */
export function useLocalBoards(projectId: string) {
  return useLoad(() => routes.localBoards(projectId).then((r) => r.boards).catch(() => [] as LocalBoardStanding[]), [projectId], 30_000);
}

/** The workbench mirror, in words: "mirrored to GitHub Issues owner/repo". */
const mirrorWords = (b: LocalBoardStanding) => (b.mirror ? `mirrored to ${PROVIDER_TEXT[b.mirror.provider]} ${b.mirror.target}` : undefined);

/** "GitHub Issues · Mojo-Up/mju-aioe", "Azure Boards · Platform" or "Jira · OPS". */
function whereOf(t: ProjectTracker, source: ProjectSource): string {
  const target = t.provider === 'github-issues' ? (t.repository ?? source.repositories.find((r) => r.primary)?.name ?? source.repositories[0]?.name ?? '') : t.provider === 'jira' ? (t.jiraProject ?? '') : (t.azureProject ?? source.devopsProject ?? '');
  return `${PROVIDER_TEXT[t.provider]} · ${target}`;
}

export function TrackersPanel({ projectId, source, owner, onChange }: { projectId: string; source: ProjectSource | null; owner: boolean; onChange: () => void }) {
  const tone = VIEW_TONES.projects!;
  const standing = useLoad(() => routes.trackers(projectId).catch(() => ({ trackers: [] as TrackerStanding[] })), [projectId, source?.trackers.length ?? 0], 30_000);
  const boards = useLoad(() => routes.projectBoards(projectId).catch(() => []), [projectId]);
  const local = useLocalBoards(projectId);
  const kept = local.data ?? [];
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ProjectTracker>();
  const [statuses, setStatuses] = useState<{ id: string; list?: TrackerStatus[] }>();
  const showStatuses = async (id: string) => {
    if (statuses?.id === id) return setStatuses(undefined);
    setStatuses({ id });
    setError(undefined);
    try {
      setStatuses({ id, list: (await routes.trackerStatuses(projectId, id)).statuses });
    } catch (e) {
      setStatuses(undefined);
      setError((e as Error).message);
    }
  };
  const rows = standing.data?.trackers ?? [];
  const act = async (key: string, f: () => Promise<unknown>) => {
    setBusy(key);
    setError(undefined);
    try {
      await f();
      standing.reload();
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const save = (trackers: ProjectTracker[]) => routes.setProjectSource(projectId, { ...source!, trackers });
  if (!source) return null;
  return (
    <>
    <ListGroup
      title="Issue trackers"
      count={rows.length || undefined}
      description="The platform keeps a board and the tracker's issues the same: pulled on the provider's webhooks and every hour, pushed when the board changes."
      actions={owner && !adding ? <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>Add a tracker</Button> : undefined}
      empty={rows.length === 0 && !adding && !error && !standing.error ? <Empty tone={tone} icon={<ArrowsRotateRight />} title="No tracker yet" body={owner ? "Add one to keep a board in step with the repository's issues, the DevOps project's work items or a Jira project." : 'An owner can keep a board in step with the tracker.'} compact /> : undefined}
    >
          {rows.map((s) => {
            const mark = busy === `sync:${s.tracker.id}` ? ({ state: 'working', label: 'Syncing…' } as const) : s.errors.length ? ({ state: 'error', label: `Trouble · ${timeAgo(s.errors[0]!.at)}` } as const) : s.lastSyncAt ? ({ state: 'connected', label: `Synced ${timeAgo(s.lastSyncAt)}` } as const) : ({ state: 'idle', label: 'Not synced yet' } as const);
            return (
              <ListRow
                key={s.tracker.id}
                title={whereOf(s.tracker, source)}
                description={
                  <>
                    Board {s.tracker.board.board} in {s.tracker.board.rootId} · {DIRECTION_TEXT[s.tracker.direction]}
                    {s.tracker.filter?.labels?.length ? ` · labelled ${s.tracker.filter.labels.join(', ')}` : ''}
                    {` · ${s.linked} linked`}
                    {s.lastWebhookAt ? ` · webhook ${timeAgo(s.lastWebhookAt)}` : ''}
                    {s.errors[0] ? <span className="block text-danger">{s.errors[0].message}</span> : null}
                    {statuses?.id === s.tracker.id ? <span className="mt-1 block">{statuses.list ? (statuses.list.length ? `On the board: ${statuses.list.map((x) => `${x.name} is ${BOARD_TEXT[x.board]}`).join(' · ')}` : 'The Jira project has no statuses to show') : 'Asking Jira for its statuses…'}</span> : null}
                  </>
                }
                status={<StatusMark state={mark.state} label={mark.label} />}
                compactControl
                control={
                  <RowMenu
                    label={`More for ${whereOf(s.tracker, source)}`}
                    items={[
                      s.tracker.provider === 'jira' && { id: 'statuses', label: statuses?.id === s.tracker.id ? 'Hide statuses' : 'Show its statuses' },
                      owner && { id: 'sync', label: 'Sync now', disabled: Boolean(busy) },
                      owner && { id: 'remove', label: 'Remove…', danger: true, disabled: Boolean(busy) },
                    ]}
                    onAction={(k) => (k === 'statuses' ? void showStatuses(s.tracker.id) : k === 'sync' ? void act(`sync:${s.tracker.id}`, () => routes.syncTracker(projectId, s.tracker.id)) : setRemoving(s.tracker))}
                  />
                }
              />
            );
          })}
      {adding && (
        <AddTracker
          local={kept}
          source={source}
          boards={(boards.data ?? []).filter((b) => b.rootId !== '').map((b) => ({ rootId: b.rootId, board: b.board }))}
          busy={busy === 'add'}
          onCancel={() => setAdding(false)}
          onAdd={(t) => void act('add', () => save([...source.trackers, t])).then(() => setAdding(false))}
        />
      )}
      {(error || standing.error) && (
        <ListBlock>
          <ErrorNote error={error ?? standing.error} />
        </ListBlock>
      )}
    </ListGroup>
      {kept.length > 0 && (
        <ListGroup title="Kept on a workbench" count={kept.length} description="These boards live on a person's workbench, not here. Where a workbench mirrors one to an issue tracker, the platform adds no second tracker for the same place.">
          {kept.map((b) => (
            <ListRow
              key={`${b.device}/${b.rootId}`}
              icon={<Display />}
              title={`${b.board ? `Board ${b.board}` : 'Main board'} in ${b.repositoryName ?? b.rootId}`}
              description={`Kept on ${b.alsoOn?.length ? `${b.deviceName} and ${b.alsoOn.length === 1 ? b.alsoOn[0] : `${b.alsoOn.length} other workbenches`}` : b.deviceName}${mirrorWords(b) ? ` · ${mirrorWords(b)}` : ''}`}
              status={<StatusMark state={b.agents > 0 ? 'working' : 'idle'} label={b.agents > 0 ? `${plural(b.agents, 'agent')} busy` : 'No agent busy'} />}
            />
          ))}
        </ListGroup>
      )}
      <ConfirmDialog
        isOpen={!!removing}
        title={removing ? `Stop keeping ${whereOf(removing, source)} in step` : 'Stop keeping in step'}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const t = removing;
          setRemoving(undefined);
          if (t) await act(`remove:${t.id}`, () => save(source.trackers.filter((x) => x.id !== t.id)));
        }}
      >
        Linked tasks keep their links; nothing is deleted at either end.
      </ConfirmDialog>
    </>
  );
}

/** The form: where (provider and repository or DevOps project), which board, which way, and an optional label filter. */
function AddTracker({ source, local, boards, busy, onCancel, onAdd }: { source: ProjectSource; local: LocalBoardStanding[]; boards: Array<{ rootId: string; board: string }>; busy: boolean; onCancel: () => void; onAdd: (t: ProjectTracker) => void }) {
  const suggested: TrackerProvider = source.provider === 'azure-devops' ? 'azure-boards' : 'github-issues';
  const [provider, setProvider] = useState<TrackerProvider>(suggested);
  const jiraConnections = useLoad(() => routes.namedSourceConnections().then((l) => l.filter((c) => c.provider === 'jira')).catch(() => []), []);
  const [connectionId, setConnectionId] = useState<string>();
  const [jiraKey, setJiraKey] = useState('');
  const [issueType, setIssueType] = useState('');
  const jira = provider === 'jira';
  const jiraConnection = connectionId ?? jiraConnections.data?.[0]?.id;
  const [target, setTarget] = useState(source.provider === 'azure-devops' ? (source.devopsProject ?? '') : (source.repositories.find((r) => r.primary)?.name ?? source.repositories[0]?.name ?? ''));
  const [board, setBoard] = useState(boards[0] ? `${boards[0].rootId}/${boards[0].board}` : '');
  const [direction, setDirection] = useState<'two-way' | 'import'>('two-way');
  const [labels, setLabels] = useState('');
  const chosen = boards.find((b) => `${b.rootId}/${b.board}` === board);
  const where = jira ? jiraKey.trim() : target.trim();
  // A workbench that mirrors this very place from a local board is a second writer: the platform will refuse.
  const mirroring = where ? local.find((b) => b.mirror?.provider === provider && trackerTargetKey(provider, b.mirror.target) === trackerTargetKey(provider, where)) : undefined;
  const ready = (jira ? /^[A-Z][A-Z0-9_]*$/.test(jiraKey.trim()) && !!jiraConnection : target.trim().length > 0) && !!chosen;
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 @[35rem]:grid-cols-2">
        <label className="grid gap-1 text-xs text-muted">
          Tracker
          <ChoiceSelect label="Tracker" className="w-full" value={provider} options={Object.fromEntries(TRACKER_PROVIDERS.map((p) => [p, PROVIDER_TEXT[p]])) as Record<TrackerProvider, string>} onChange={setProvider} />
        </label>
        {jira ? (
          <label className="grid gap-1 text-xs text-muted">
            Jira project key
            <input className={`${inputClass} mono`} value={jiraKey} onChange={(e) => setJiraKey(e.target.value.toUpperCase())} placeholder="OPS" autoComplete="off" />
          </label>
        ) : (
          <label className="grid gap-1 text-xs text-muted">
            {provider === 'github-issues' ? 'Repository (owner/name)' : 'DevOps project'}
            <input className={inputClass} value={target} onChange={(e) => setTarget(e.target.value)} placeholder={provider === 'github-issues' ? 'Mojo-Up/mju-aioe' : 'Platform'} />
          </label>
        )}
        {jira && (
          <label className="grid gap-1 text-xs text-muted">
            Jira site
            <ChoiceSelect
              label="Jira site"
              className="w-full"
              value={jiraConnection}
              placeholder={jiraConnections.data?.length === 0 ? 'No Jira connection yet: an administrator adds one in Settings' : undefined}
              isDisabled={!jiraConnections.data?.length}
              options={Object.fromEntries((jiraConnections.data ?? []).map((c) => [c.id, `${c.name} (${c.baseUrl.replace(/^https?:\/\//, '')})`]))}
              onChange={setConnectionId}
            />
          </label>
        )}
        {jira && (
          <label className="grid gap-1 text-xs text-muted">
            New board tasks become (optional)
            <input className={inputClass} value={issueType} onChange={(e) => setIssueType(e.target.value)} placeholder="Task" />
          </label>
        )}
        <label className="grid gap-1 text-xs text-muted">
          Board
          <ChoiceSelect
            label="Board"
            className="w-full"
            value={board || undefined}
            placeholder={boards.length === 0 ? 'No board has been used yet' : undefined}
            isDisabled={boards.length === 0}
            options={Object.fromEntries(boards.map((b) => [`${b.rootId}/${b.board}`, `${b.board} in ${b.rootId}`]))}
            onChange={setBoard}
          />
          {mirroring ? (
            <span role="alert" className="text-warning">
              {mirroring.deviceName} keeps a board locally and mirrors it to {PROVIDER_TEXT[provider]} {mirroring.mirror!.target}. Switch that mirror off there, or promote the board, then add the tracker.
            </span>
          ) : local.length > 0 ? (
            <span>
              {local.length === 1 ? `${local[0]!.deviceName} keeps the board for ${local[0]!.repositoryName ?? local[0]!.rootId} locally` : `${plural(local.length, 'board')} are kept on workbenches`}, so ${local.length === 1 ? 'it is' : 'they are'} not listed here. Promote ${local.length === 1 ? 'it' : 'one'} to use with a tracker.
            </span>
          ) : null}
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Which way
          <ChoiceSelect
            label="Which way"
            className="w-full"
            value={direction}
            options={{ 'two-way': 'Two-way: board changes go to the tracker too', import: 'Import only: the tracker is read, never written' }}
            onChange={setDirection}
          />
        </label>
      </div>
      <label className="grid gap-1 text-xs text-muted">
        Only items with these labels (optional, comma-separated)
        <input className={inputClass} value={labels} onChange={(e) => setLabels(e.target.value)} placeholder="agent, bug" />
      </label>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          isDisabled={!ready || !!mirroring}
          isPending={busy}
          onPress={() => {
            const id = `${provider === 'github-issues' ? 'gh' : jira ? 'jira' : 'ado'}-${where.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`.slice(0, 60);
            const filter = labels.split(',').map((x) => x.trim()).filter(Boolean);
            const at = jira ? { connectionId: jiraConnection!, jiraProject: where, ...(issueType.trim() ? { issueType: issueType.trim() } : {}) } : provider === 'github-issues' ? { repository: where } : { azureProject: where };
            onAdd({ id, provider, ...at, board: chosen!, direction, ...(filter.length ? { filter: { labels: filter } } : {}) });
          }}
        >
          Keep in step
        </Button>
        <Button size="sm" variant="ghost" onPress={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
